import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, MenuItem, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import { Save, ArrowBack, Add, Delete } from '@mui/icons-material';
import procurementService from '../../services/procurement.service';
import settingsService from '../../services/settings.service';
import { useLanguage } from '../../i18n/LanguageContext';

interface BillLine {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

const emptyLine: BillLine = { description: '', quantity: 1, unitPrice: 0, taxRate: 0 };

const PurchaseInvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [form, setForm] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    supplierId: '' as any,
    purchaseOrderId: '' as any,
    currencyCode: 'USD',
    exchangeRate: 1,
    supplierInvoiceNumber: '',
    notes: ''
  });
  const [lines, setLines] = useState<BillLine[]>([{ ...emptyLine }]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [supRes, poRes, currRes] = await Promise.all([
        procurementService.getSuppliers(),
        procurementService.getPurchaseOrders('APPROVED'),
        settingsService.getCurrencies().catch(() => ({ data: [] }))
      ]);
      setSuppliers(supRes.data);
      // Also load ORDERED and RECEIVED POs
      const [poOrdered, poReceived] = await Promise.all([
        procurementService.getPurchaseOrders('ORDERED').catch(() => ({ data: [] })),
        procurementService.getPurchaseOrders('RECEIVED').catch(() => ({ data: [] }))
      ]);
      setPurchaseOrders([...poRes.data, ...poOrdered.data, ...poReceived.data]);
      setCurrencies(currRes.data);
      const def = currRes.data.find((c: any) => c.isDefault);
      if (def) setForm(f => ({ ...f, currencyCode: def.code }));
    } catch (err) { console.error(err); }
  };

  const handlePOSelect = async (poId: number) => {
    if (!poId) {
      setForm(f => ({ ...f, purchaseOrderId: '' }));
      return;
    }
    try {
      const res = await procurementService.getPurchaseOrder(poId);
      const po = res.data;
      setForm(f => ({
        ...f,
        purchaseOrderId: poId,
        supplierId: po.supplierId,
        currencyCode: po.currencyCode || f.currencyCode,
      }));
      if (po.lines) {
        setLines(po.lines.map((l: any) => ({
          description: l.productName,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRate: l.taxRate
        })));
      }
    } catch (err) { console.error(err); }
  };

  const updateLine = (index: number, field: keyof BillLine, value: any) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    setLines(updated);
  };

  const addLine = () => setLines([...lines, { ...emptyLine }]);
  const removeLine = (i: number) => { if (lines.length > 1) setLines(lines.filter((_, idx) => idx !== i)); };

  const calcLineTotal = (l: BillLine) => { const net = l.quantity * l.unitPrice; return net + net * l.taxRate / 100; };
  const subTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const taxTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice * l.taxRate / 100, 0);
  const grandTotal = subTotal + taxTotal;
  const sym = currencies.find(c => c.code === form.currencyCode)?.symbol || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    const validLines = lines.filter(l => l.description && l.quantity > 0);
    if (!validLines.length) { setError('At least one line item is required'); return; }
    if (!form.supplierId) { setError('Please select a supplier'); return; }
    try {
      await procurementService.createPurchaseInvoice({
        ...form,
        purchaseOrderId: form.purchaseOrderId || null,
        lines: validLines
      });
      navigate('/purchase-invoices');
    } catch (err: any) { setError(err.response?.data?.message || 'Error creating bill'); }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">New Purchase Invoice (Bill)</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/purchase-invoices')}>Back</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        Optionally select a Purchase Order to auto-fill the bill lines, or create a standalone bill.
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth select label="From Purchase Order (optional)" value={form.purchaseOrderId}
          onChange={e => handlePOSelect(Number(e.target.value))}>
          <MenuItem value="">— None (standalone bill) —</MenuItem>
          {purchaseOrders.map((po: any) => (
            <MenuItem key={po.id} value={po.id}>{po.orderNumber} — {po.supplierName} ({po.status})</MenuItem>
          ))}
        </TextField>
        <TextField select label={t('common.currency')} value={form.currencyCode} sx={{ minWidth: 150 }}
          onChange={e => setForm({ ...form, currencyCode: e.target.value })}>
          {currencies.map((c: any) => <MenuItem key={c.id} value={c.code}>{c.code} ({c.symbol})</MenuItem>)}
          {currencies.length === 0 && <MenuItem value="USD">USD ($)</MenuItem>}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required select label={t('purchase_orders.supplier')} value={form.supplierId}
          onChange={e => setForm({ ...form, supplierId: e.target.value })}>
          <MenuItem value="">Select Supplier</MenuItem>
          {suppliers.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.code} — {s.name}</MenuItem>)}
        </TextField>
        <TextField fullWidth label={t('purchase_invoices.supplier_invoice')} value={form.supplierInvoiceNumber}
          onChange={e => setForm({ ...form, supplierInvoiceNumber: e.target.value })}
          placeholder="Vendor's own reference number" />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required type="date" label={t('purchase_invoices.invoice_date')} value={form.invoiceDate}
          onChange={e => setForm({ ...form, invoiceDate: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField fullWidth required type="date" label={t('invoices.due_date')} value={form.dueDate}
          onChange={e => setForm({ ...form, dueDate: e.target.value })} InputLabelProps={{ shrink: true }} />
      </Box>

      <TextField fullWidth label={t('common.notes')} value={form.notes}
        onChange={e => setForm({ ...form, notes: e.target.value })} sx={{ mb: 2 }} />

      <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>Bill Lines</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>{t('common.description')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.quantity')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.unit_price')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('sales_orders.tax_pct')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Line Total</TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <TextField fullWidth size="small" placeholder="Description" value={line.description}
                    onChange={e => updateLine(idx, 'description', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField fullWidth size="small" type="number" value={line.quantity}
                    onChange={e => updateLine(idx, 'quantity', Number(e.target.value))} inputProps={{ min: 1 }} />
                </TableCell>
                <TableCell>
                  <TextField fullWidth size="small" type="number" value={line.unitPrice}
                    onChange={e => updateLine(idx, 'unitPrice', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} />
                </TableCell>
                <TableCell>
                  <TextField fullWidth size="small" type="number" value={line.taxRate}
                    onChange={e => updateLine(idx, 'taxRate', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{calcLineTotal(line).toFixed(2)}</TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={3} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('common.subtotal')}:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{subTotal.toFixed(2)}</TableCell><TableCell />
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={3} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('common.tax')}:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{taxTotal.toFixed(2)}</TableCell><TableCell />
            </TableRow>
            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
              <TableCell colSpan={3} /><TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{t('common.total')}:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{grandTotal.toFixed(2)}</TableCell><TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Button startIcon={<Add />} onClick={addLine} sx={{ mt: 1 }}>{t('common.add_line')}</Button>
      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" startIcon={<Save />} size="large">{t('purchase_invoices.create')}</Button>
      </Box>
    </Box>
  );
};

export default PurchaseInvoiceForm;
